// 客（宇宙人）の 3D 表現。パステル低ポリの「もこもこブロブ」をベースに、
// 種ごとに色・耳・羽根・形でバリエーションを出す。
//
// デコ同様ドット絵テクスチャは作らず「形と色」で可愛さを出す（ピクセレート
// パイプラインが効くので低ポリで良い）。各ビルダーは足元 y=0 基準・原点中心の
// Group を返す。CustomerLook（customers.ts の純データ）を render に翻訳する層。

import * as THREE from 'three';
import type { CustomerLook } from '../sim/customers';

function mat(color: string, opts: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    roughness: 0.85,
    metalness: 0.0,
    flatShading: true,
    ...opts,
  });
}

/** ライティングに依存しないベタ塗り（瞳・発光部）。 */
function flat(color: string): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color: new THREE.Color(color) });
}

/**
 * もこもこ本体（icosphere を頂点ノイズで軽く崩した毛玉）。決定的に崩すので
 * 同じ見た目が安定して出る（Math.random は使わない）。
 */
function makeBlobBody(color: string, radius: number): THREE.Mesh {
  const geo = new THREE.IcosahedronGeometry(radius, 1);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    // 黄金角ベースの決定的ノイズで毛玉のでこぼこを出す
    const n = 1 + Math.sin(x * 6.1) * Math.cos(z * 5.3) * 0.12 + Math.sin(y * 7.7) * 0.06;
    pos.setXYZ(i, x * n, y * n, z * n);
  }
  geo.computeVertexNormals();
  const m = new THREE.Mesh(geo, mat(color, { roughness: 0.95 }));
  return m;
}

/**
 * 顔（ふたつの黒い瞳＋白いハイライト、うすピンクのほっぺ）を本体に付ける。
 * 可愛さの肝なので、瞳は大きめ・ほっぺは控えめのうすピンクにする。
 */
function addFace(g: THREE.Group, bodyRadius: number): void {
  const r = bodyRadius;
  for (const sx of [-1, 1]) {
    // 黒い瞳（大きめのドット）
    const eye = new THREE.Mesh(new THREE.SphereGeometry(r * 0.17, 10, 8), flat('#3A2F4A'));
    eye.position.set(sx * 0.3 * r, r * 0.12, r * 0.92);
    g.add(eye);
    // 白いきらめき（瞳の上にちょこんと）
    const shine = new THREE.Mesh(new THREE.SphereGeometry(r * 0.06, 8, 6), flat('#FFFFFF'));
    shine.position.set(sx * 0.3 * r + r * 0.05, r * 0.2, r * 1.04);
    g.add(shine);
    // うすピンクのほっぺ
    const cheek = new THREE.Mesh(new THREE.CircleGeometry(r * 0.12, 12), flat('#FF9FBE'));
    cheek.position.set(sx * 0.52 * r, r * -0.06, r * 0.9);
    cheek.rotation.y = sx * 0.5;
    (cheek.material as THREE.MeshBasicMaterial).transparent = true;
    (cheek.material as THREE.MeshBasicMaterial).opacity = 0.8;
    g.add(cheek);
  }
}

/** 種ごとの差分パーツを足す。 */
function addShapeParts(g: THREE.Group, look: CustomerLook, r: number): void {
  switch (look.shape) {
    case 'moth': {
      // 左右の羽根（薄い四角錐を寝かせる）
      for (const side of [-1, 1]) {
        const wing = new THREE.Mesh(
          new THREE.ConeGeometry(r * 0.6, r * 1.1, 4),
          mat(look.accentColor, { transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
        );
        wing.scale.set(1, 0.12, 1);
        wing.position.set(side * r * 0.9, r * 0.5, -r * 0.1);
        wing.rotation.z = side * Math.PI * 0.25;
        wing.rotation.x = Math.PI / 2;
        g.add(wing);
      }
      // 触角
      for (const side of [-1, 1]) {
        const ant = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, r * 0.7, 4), mat('#5A4A6A'));
        ant.position.set(side * r * 0.18, r * 1.05, r * 0.2);
        ant.rotation.z = side * 0.4;
        g.add(ant);
      }
      break;
    }
    case 'crystal': {
      // 背中から生える結晶の角
      for (const [sx, h] of [[0, 1.0], [-0.3, 0.7], [0.3, 0.7]] as const) {
        const spike = new THREE.Mesh(
          new THREE.OctahedronGeometry(r * 0.22),
          mat(look.accentColor, { roughness: 0.3, metalness: 0.15, transparent: true, opacity: 0.9 }),
        );
        spike.scale.set(0.7, h, 0.7);
        spike.position.set(sx * r, r * 0.9, -r * 0.4);
        g.add(spike);
      }
      break;
    }
    case 'drop': {
      // 雫型: 頭頂にしずくのとんがり（ひと目で水滴と分かる）
      const tip = new THREE.Mesh(new THREE.ConeGeometry(r * 0.4, r * 0.9, 10), mat(look.accentColor));
      tip.position.y = r * 1.15;
      g.add(tip);
      break;
    }
    case 'crescent': {
      // 三日月の角ふたつ（夢見星人ユメミ）。トーラスを半分使って角にする。
      for (const side of [-1, 1]) {
        const horn = new THREE.Mesh(
          new THREE.TorusGeometry(r * 0.28, r * 0.06, 6, 10, Math.PI * 1.1),
          mat(look.accentColor, { roughness: 0.6 }),
        );
        horn.position.set(side * r * 0.42, r * 1.0, 0);
        horn.rotation.z = side * 0.5 + (side < 0 ? Math.PI : 0);
        horn.rotation.y = side * 0.3;
        g.add(horn);
      }
      break;
    }
    case 'sprig': {
      // 頭にちょこんと小枝＋葉っぱ（樹守ねむ）。茶の枝に緑の葉を散らす。
      const branch = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.04, r * 0.05, r * 0.55, 5), mat(look.accentColor));
      branch.position.y = r * 1.2;
      branch.rotation.z = 0.2;
      g.add(branch);
      for (const [lx, ly, s] of [[0, 1.45, 0.16], [0.12, 1.32, 0.12], [-0.1, 1.3, 0.1]] as const) {
        const leaf = new THREE.Mesh(new THREE.SphereGeometry(r * s, 6, 5), mat('#9CCB8A', { roughness: 0.95 }));
        leaf.position.set(lx * r, r * ly, 0);
        g.add(leaf);
      }
      break;
    }
    case 'blob':
    default: {
      // 基本のもこもこ: 小さなアクセントの房を頭に
      const tuft = new THREE.Mesh(new THREE.SphereGeometry(r * 0.24, 8, 6), mat(look.accentColor, { roughness: 0.95 }));
      tuft.position.y = r * 1.0;
      g.add(tuft);
      break;
    }
  }
}

/**
 * CustomerLook から客の Group を作る。足元 y=0 基準、原点中心。
 * 返る Group はおよそ半径 0.3 前後（1 セルに収まる程度）。
 */
export function buildCustomerMesh(look: CustomerLook): THREE.Group {
  const g = new THREE.Group();
  // 低解像度ピクセレートでも顔が読めるよう、やや大きめに。
  const r = 0.32 * look.scale;
  const body = makeBlobBody(look.bodyColor, r);
  body.position.y = r; // 足元を地面に乗せる
  g.add(body);
  // 顔（瞳・ハイライト・ほっぺ）は本体の中心高さに合わせて足す。
  const faceAnchor = new THREE.Group();
  faceAnchor.position.y = r;
  addFace(faceAnchor, r);
  g.add(faceAnchor);
  // 差分パーツは本体に追従させるため body 中心高さに合わせて足す。
  const partsAnchor = new THREE.Group();
  partsAnchor.position.y = r;
  addShapeParts(partsAnchor, look, r);
  g.add(partsAnchor);
  return g;
}
